import {BlockMath, InlineMath} from "react-katex";

const GraphTheory = () => {
    return (
        <div className="max-w-4xl mx-auto px-4">
            <h1 className="tw-header font-bold text-gray-900 mb-4">Graph Theory</h1>
            <p className="text-gray-600 tw-subtitle text-justify">
                Graph theory is the mathematical study of networks — how things connect to each other.
                Think of it like mapping relationships: your social network, roads between cities,
                or even how web pages link together. In graph theory, we call these connections
                "graphs," but they're not the line charts you might be thinking of.
            </p>
            <hr className="mt-2 mb-2"/>

            <p className="tw-subtitle text-gray-600">
                A graph consists of two main components: <strong>vertices</strong> (also called nodes)
                and <strong>edges</strong> (the connections between them). We can represent this formally as:
            </p>

            <div className="overflow-x-auto">
                <BlockMath math="G = (V, E)"/>
            </div>

            <p className="tw-subtitle text-gray-600">
                Where <InlineMath math="V"/> is the set of vertices and <InlineMath math="E"/> is the set of edges.
                For example, if we have 4 people in a social network, we might have:
            </p>

            <div className="overflow-x-auto">
                <BlockMath math="V = \{Alice, Bob, Carol, Dave\}"/>
            </div>
            <div className="overflow-x-auto">
                <BlockMath math="E = \{(Alice, Bob), (Bob, Carol), (Carol, Dave)\}"/>
            </div>

            <p className="tw-sub-header">Key Concepts</p>
            <br/>

            <p className="tw-subtitle text-gray-600">
                <strong>Degree</strong> of a vertex is how many connections it has. In our social network,
                Bob has degree 2 (connected to Alice and Carol), while Alice and Dave each have degree 1.
            </p>

            <div className="overflow-x-auto">
                <BlockMath math="deg(v) = \text{number of edges connected to vertex } v"/>
            </div>

            <p className="tw-subtitle text-gray-600">
                <strong>Path</strong> is a sequence of vertices where each adjacent pair is connected by an edge.
                A path from Alice to Dave would be: Alice → Bob → Carol → Dave.
            </p>

            <p className="tw-subtitle text-gray-600">
                <strong>Cycle</strong> is a path that starts and ends at the same vertex. If Carol also
                knew Alice directly, we'd have a cycle: Alice → Bob → Carol → Alice.
            </p>

            <hr className="mt-2 mb-2"/>

            <p className="tw-sub-header">Types of Graphs</p>

            <br/>

            <p className="tw-subtitle text-gray-600">
                <strong>Directed vs Undirected:</strong> In undirected graphs, relationships go both ways
                (like friendship). In directed graphs, they have a direction (like Twitter follows).
            </p>

            <p className="tw-subtitle text-gray-600">
                <strong>Weighted graphs</strong> assign values to edges — like distances between cities
                or strength of relationships.
            </p>

        </div>
    )
}

export default GraphTheory;